import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import VideoCard from '@/components/base/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import { useState } from 'react';

export default function Home() {
  const [activeTab, setActiveTab] = useState('all');
  const featuredVideos = videos.filter((v) => v.isFeatured);
  const newVideos = videos.filter((v) => v.isNew);
  const trendingVideos = [...videos].sort((a, b) => b.views - a.views).slice(0, 8);

  const filteredVideos =
    activeTab === 'all'
      ? videos.slice(0, 8)
      : videos.filter((v) => v.categorySlug === activeTab).slice(0, 8);

  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  const homeSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '蜜桃tv',
      url: 'https://videomio.github.io',
      logo: 'https://videomio.github.io/logo.png',
      description:
        '蜜桃tv是备受用户喜爱的视频内容平台，提供海量精选影视、综艺、短视频资源，高清流畅播放。',
      sameAs: ['https://weibo.com/mitaotv', 'https://space.bilibili.com/mitaotv'],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '400-888-6666',
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '蜜桃tv',
      url: 'https://videomio.github.io',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://videomio.github.io/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '蜜桃tv - 精选视频内容平台官方入口',
      description:
        '蜜桃tv是备受用户喜爱的视频内容平台，提供海量精选影视、综艺、短视频资源，高清流畅播放，随时随地畅享优质内容。',
      url: 'https://videomio.github.io',
    },
  ];

  return (
    <>
      <SEOHead
        title="蜜桃tv - 精选视频内容平台官方入口 | 海量影视综艺短视频高清在线观看"
        description="蜜桃tv是备受用户喜爱的视频内容平台，提供海量精选影视、综艺、短视频资源，高清流畅播放，随时随地畅享优质内容。立即访问蜜桃tv，开启全新视听体验。"
        keywords="蜜桃tv,蜜桃tv官网,蜜桃tv在线观看,蜜桃tv最新视频,蜜桃tv高清播放,蜜桃tv入口,蜜桃tv会员"
        canonicalUrl="https://videomio.github.io"
        schemaData={homeSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20cinematic%20abstract%20background%20with%20soft%20peach%20and%20coral%20gradient%20tones%2C%20gentle%20flowing%20curves%2C%20smooth%20bokeh%20lights%2C%20elegant%20modern%20atmosphere%2C%20subtle%20glass-like%20texture%2C%20minimalist%20and%20sophisticated%2C%20warm%20golden%20light%20rays%2C%20peaceful%20dreamy%20mood&width=1800&height=900&seq=mitao-hero-bg&orientation=landscape"
              alt="蜜桃tv背景"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="relative z-10 w-full px-4 md:px-6 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/20 mb-6">
                  <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse"></span>
                  <span className="text-background-50 text-sm font-medium">
                    精选好内容，温暖每一刻
                  </span>
                </div>
                <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 mb-4 leading-tight">
                  发现属于你的
                  <br />
                  <span className="text-accent-400">精彩视界</span>
                </h1>
                <p className="text-base md:text-lg text-background-200 mb-8 max-w-xl mx-auto leading-relaxed">
                  海量精选影视、综艺、动漫、短视频 —— 高清流畅，随时随地畅享优质内容
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/category/dianying"
                    className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
                  >
                    立即探索
                  </Link>
                  <Link
                    to="/about"
                    className="px-8 py-3 rounded-full bg-background-50/15 text-background-50 font-medium text-sm hover:bg-background-50/25 transition-all duration-300 border border-background-50/20 whitespace-nowrap cursor-pointer backdrop-blur-sm"
                  >
                    了解更多
                  </Link>
                </div>
                {/* Stats */}
                <div className="flex items-center justify-center gap-8 md:gap-12 mt-10">
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                      5000万+
                    </div>
                    <div className="text-xs md:text-sm text-background-300 mt-1">
                      累计用户
                    </div>
                  </div>
                  <div className="w-px h-10 bg-background-50/20"></div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                      10万+
                    </div>
                    <div className="text-xs md:text-sm text-background-300 mt-1">
                      精选内容
                    </div>
                  </div>
                  <div className="w-px h-10 bg-background-50/20"></div>
                  <div className="text-center">
                    <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">
                      100亿+
                    </div>
                    <div className="text-xs md:text-sm text-background-300 mt-1">
                      总播放量
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Category Quick Nav */}
          <section className="w-full px-4 md:px-6 py-10 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
                  分类浏览
                </h2>
                <Link
                  to="/category/dianshi"
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 transition-colors"
                >
                  查看全部
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-2 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/50 transition-all duration-300 cursor-pointer"
                  >
                    <span className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center group-hover:bg-primary-200 transition-colors duration-300">
                      <i className={`${cat.icon} text-lg text-primary-600`}></i>
                    </span>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors duration-300">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-500">
                      {cat.count}+部
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Videos */}
          <section className="w-full px-4 md:px-6 py-12">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
                  精选推荐
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {featuredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} variant="featured" />
                ))}
              </div>
            </div>
          </section>

          {/* Tab Section: All Videos */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              {/* Tabs */}
              <div className="flex items-center gap-1 mb-6 overflow-x-auto pb-2 flex-wrap">
                <div className="inline-flex items-center bg-background-50 rounded-full p-1 border border-background-200/70">
                  {[
                    { key: 'all', label: '全部' },
                    ...categories.slice(0, 6).map((c) => ({
                      key: c.slug,
                      label: c.name,
                    })),
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        activeTab === tab.key
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-950'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </section>

          {/* Trending */}
          <section className="w-full px-4 md:px-6 py-12">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
                  热门排行
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {trendingVideos.map((video, index) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.slug}`}
                    className="group flex items-center gap-4 p-3 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                  >
                    <span className="text-2xl font-heading font-bold text-foreground-300 w-8 text-center flex-shrink-0">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-28 h-16 rounded-lg object-cover flex-shrink-0"
                      title={`${video.title} - 蜜桃tv`}
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 mb-1">
                        {video.title}
                      </h4>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700">
                          {video.category}
                        </span>
                        <span>{formatViews(video.views)} 次观看</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-primary-500 flex-shrink-0">
                      <i className="ri-play-circle-fill text-xl"></i>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Latest */}
          <section className="w-full px-4 md:px-6 py-12 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="w-1 h-5 rounded-full bg-secondary-500"></span>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
                    最新上线
                  </h2>
                </div>
              </div>
              <div className="space-y-3">
                {newVideos.map((video) => (
                  <VideoCard key={video.id} video={video} variant="compact" />
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full px-4 md:px-6 py-16 relative overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20gradient%20abstract%20background%20with%20soft%20peach%20and%20amber%20tones%2C%20smooth%20flowing%20organic%20shapes%2C%20gentle%20light%20beams%2C%20modern%20elegant%20composition%2C%20peaceful%20atmosphere%2C%20minimalist%20design%2C%20warm%20inviting%20mood&width=1800&height=600&seq=mitao-cta-bg&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/40"></div>
            <div className="relative z-10 max-w-[800px] mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
                加入蜜桃tv，开启视听新体验
              </h2>
              <p className="text-sm md:text-base text-background-200 mb-8 max-w-md mx-auto">
                海量精选内容，高清流畅播放，随时随地畅享精彩视界
              </p>
              <button className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap">
                立即免费体验
              </button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}